import { Component } from "react";
import { observer } from "mobx-react";

import type { Message } from "../models.ts";

type ChatMessageModelLoadingProgressProps = {
  message: Message;
};

export function shouldShowModelLoadingProgress(message: Message): boolean {
  const hasWaitingInfo = !!message.waitingInfo;
  const hasModelLoadProgress = !!(message.modelLoadTotal && message.modelLoadTotal > 0);
  const hasPromptEvalProgress = !!(message.promptEvalTotal && message.promptEvalTotal > 0);
  if (!hasWaitingInfo && !hasModelLoadProgress && !hasPromptEvalProgress) return false;

  const modelLoadPercent = hasModelLoadProgress
    ? Math.min(100, (message.modelLoadCompleted || 0) / message.modelLoadTotal! * 100)
    : 0;
  const promptEvalPercent = hasPromptEvalProgress
    ? Math.min(100, (message.promptEvalCompleted || 0) / message.promptEvalTotal! * 100)
    : 0;

  const isWaitingForWorker = hasWaitingInfo && !hasModelLoadProgress && !message.thinking && !message.content;
  const isModelLoading = hasModelLoadProgress && modelLoadPercent < 100 && !message.thinking && !message.content;
  const isPromptEval = !isModelLoading && hasPromptEvalProgress && promptEvalPercent < 100 && !message.thinking && !message.content;
  const isTakingLonger = !isModelLoading && !isPromptEval && hasPromptEvalProgress && promptEvalPercent >= 100 && !message.thinking && !message.content;

  return isWaitingForWorker || isModelLoading || isPromptEval || isTakingLonger;
}

@observer
export class ChatMessageModelLoadingProgress extends Component<ChatMessageModelLoadingProgressProps> {
  render() {
    const { message } = this.props;
    const hasWaitingInfo = !!message.waitingInfo;
    const hasModelLoadProgress = message.modelLoadTotal && message.modelLoadTotal > 0;
    const hasPromptEvalProgress = message.promptEvalTotal && message.promptEvalTotal > 0;

    if (!hasWaitingInfo && !hasModelLoadProgress && !hasPromptEvalProgress) return null;

    const modelLoadPercent = hasModelLoadProgress
      ? Math.min(100, (message.modelLoadCompleted || 0) / message.modelLoadTotal! * 100)
      : 0;
    const promptEvalPercent = hasPromptEvalProgress
      ? Math.min(100, (message.promptEvalCompleted || 0) / message.promptEvalTotal! * 100)
      : 0;

    const formatBytes = (bytes: number) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    const isWaitingForWorker = hasWaitingInfo && !hasModelLoadProgress && !message.thinking && !message.content;
    const isModelLoading = hasModelLoadProgress && modelLoadPercent < 100 && !message.thinking && !message.content;
    const isPromptEval = !isModelLoading && hasPromptEvalProgress && promptEvalPercent < 100 && !message.thinking && !message.content;
    const isTakingLonger = !isModelLoading && !isPromptEval && hasPromptEvalProgress && promptEvalPercent >= 100 && !message.thinking && !message.content;

    if (!isWaitingForWorker && !isModelLoading && !isPromptEval && !isTakingLonger) return null;

    return (
      <div className="model-loading-progress">
        {isWaitingForWorker && (
          <div className="progress-item waiting-for-worker">
            <div className="progress-label">
              <span className="longer-spinner"></span>
              {message.waitingInfo}
            </div>
          </div>
        )}
        {isModelLoading && (
          <div className="progress-item">
            <div className="progress-label">
              Loading model: {formatBytes(message.modelLoadCompleted || 0)} / {formatBytes(message.modelLoadTotal!)}
            </div>
            <div className="progress-bar">
              <div className="progress-bar-fill progress-bar-blue" style={{ width: `${modelLoadPercent}%` }}></div>
            </div>
          </div>
        )}
        {isPromptEval && (
          <div className="progress-item">
            <div className="progress-label">
              Reading context: {message.promptEvalCompleted || 0} / {message.promptEvalTotal!} tokens
            </div>
            <div className="progress-bar">
              <div className="progress-bar-fill progress-bar-green" style={{ width: `${promptEvalPercent}%` }}></div>
            </div>
          </div>
        )}
        {isTakingLonger && (
          <div className="progress-item taking-longer">
            <div className="progress-label">
              <span className="longer-spinner"></span>
              Takes longer than expected, please wait...
            </div>
          </div>
        )}
      </div>
    );
  }
}
